import React, {useContext, useEffect, useState} from 'react';
import {Card, CardBody, CardHeader, Col, Container, Modal, ModalBody, ModalFooter, ModalHeader, Row} from 'reactstrap';
import {Breadcrumbs, Btn, H5} from '../../../../AbstractElements';
import DataTable from "react-data-table-component";
import axiosConfig from "../../../../Config/axios";
import IconBtn from "../../../../CommonElements/IconButton";
import ButtonIcon from "../../../../CommonElements/ButtonIcon";
import Loader from "../../../../Layout/Loader";
import RoleForm from "../Component/Form";
import SearchOptions from "../../../CommonComponents/Search";
import AuthorizationContext from "../../../../_helper/Authorization";
import type {Role} from "../Types/RoleTypes";
import type {AuthorizationContextType} from "../../../../_helper/Authorization/AuthorizationTypes";

const RoleIndex = () => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [roles, setRoles] = useState<Role[]>([]);
    const [roleModalVisibility, setRoleModalVisibility] = useState<boolean>(false);
    const [searchVisibility, setSearchVisibility] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [totalRows, setTotalRows] = useState<number>(0);
    const searchOptions = [{name: 'name', label: 'Key'}, {name: 'display_name', label: 'Name'}, {name: 'description'}];
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler} = authContext || {};

    const roleModalToggle = () => {
        setRoleModalVisibility(!roleModalVisibility);
    }

    const getRoles = (params: any, page: number = currentPage, limit: number = perPage) => {
        setLoaderVisibility(true);
        const config = {params: {...params, page, per_page: limit}};
        axiosConfig
            .get(`/privilege/role`, config)
            .then((response: any) => {
                if (response?.data?.data?.data) {
                    setRoles(response.data.data.data);
                }
                if (response?.data?.data?.meta) {
                    setCurrentPage(response.data.data.meta.current_page);
                    setPerPage(response.data.data.meta.per_page);
                    setTotalRows(response.data.data.meta.total);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }
    const deleteRole = (id: number) => {
        setLoaderVisibility(true);
        axiosConfig
            .delete(`/privilege/role/${id}`)
            .then((response: { data: { data: Role}; }) => {
                if (response?.data?.data) {
                    setRoles(current => current.filter(role => {
                        return role.id !== response?.data?.data?.id;
                    }),);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    const addRole = (role: Role) => {
        const rolesList = [...roles];
        rolesList.push(role);
        setRoles(rolesList);
        roleModalToggle();
    }

    useEffect(() => {
        getRoles({}, 1, perPage);
    }, [])

    const roleTableColumns = [{
        name: 'Key', selector: (row: Role) => row.name, sortable: false,
    }, {
        name: 'Name', selector: (row: Role) => row.display_name, sortable: true,
    }, {
        name: 'Description', selector: (row: Role) => row.description, sortable: true, center: true,
    }, {
        name: 'Option', selector: (row: Role) => <div className='d-flex align-items-right gap-2'>
            <IconBtn link={`/privilege/role/${row.id}`} type={'view'} permission={'read-role'}/>
            {!row.system_defined && <IconBtn confirm={true} onclick={() => deleteRole(row.id)} type={'delete'} permission={'delete-role'}/>}
        </div>, sortable: false, right: true,
    }];

    return (<div>
            <Breadcrumbs mainTitle="Roles" parent="Privilege" title="Roles"/>
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <Loader visibility={loaderVisibility}/>
                            <CardHeader className='d-flex align-items-center justify-content-between'>
                                <H5>Roles</H5>
                                <div>
                                    <div className='d-flex align-items-center gap-2'>
                                        <ButtonIcon attrBtn={{
                                            onClick: () => setRoleModalVisibility(true)
                                        }} type={'add'} permission={'create-role'}/>

                                        <ButtonIcon attrBtn={{
                                            onClick: () => getRoles({}, currentPage, perPage)
                                        }} type={'sync'} permission={'read-role'}/>
                                        <ButtonIcon attrBtn={{
                                            onClick: () => setSearchVisibility(!searchVisibility)
                                        }} type={'search'} permission={'read-role'}/>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardBody>
                                {searchVisibility && <SearchOptions callBack={(params: any) => getRoles(params, 1, perPage)}
                                                                    searchOptions={searchOptions}/>}
                                <DataTable pagination paginationServer columns={roleTableColumns} data={roles}
                                           paginationTotalRows={totalRows}
                                           paginationPerPage={perPage}
                                           paginationDefaultPage={currentPage}
                                           onChangePage={(page) => getRoles({}, page, perPage)}
                                           onChangeRowsPerPage={(newPerPage, page) => getRoles({}, page, newPerPage)}
                                           highlightOnHover={true}
                                           striped={true} responsive={true}/>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <Modal isOpen={roleModalVisibility} toggle={roleModalToggle} centered>
                <ModalHeader>
                    Create Role
                </ModalHeader>
                <ModalBody>
                    <RoleForm callBack={addRole}/>
                </ModalBody>
                <ModalFooter>
                    <Btn attrBtn={{color: 'primary', onClick: roleModalToggle}}>Close</Btn>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default RoleIndex;
